import type {
  PublishedPortalSnapshotRecord,
  PublishedPortalWeekRecord,
  PublishedPortalWeekSkinsRowRecord,
} from '../api/types.js';

function compareWeeksAscending(left: PublishedPortalWeekRecord, right: PublishedPortalWeekRecord): number {
  const dateDiff = left.date.localeCompare(right.date);
  return dateDiff !== 0 ? dateDiff : left.matchDayId.localeCompare(right.matchDayId);
}

export function sortPublishedWeeks(snapshot: PublishedPortalSnapshotRecord): PublishedPortalWeekRecord[] {
  return [...snapshot.weeks].sort(compareWeeksAscending);
}

export function selectLatestCompletedPublishedWeek(
  snapshot: PublishedPortalSnapshotRecord,
): PublishedPortalWeekRecord | null {
  return (
    sortPublishedWeeks(snapshot)
      .filter((week) => week.completedMatches > 0 || (Array.isArray(week.skinsRows) && week.skinsRows.length > 0))
      .at(-1) ?? null
  );
}

export function selectNextPublishedWeek(
  snapshot: PublishedPortalSnapshotRecord,
  selectedWeekId: string,
): PublishedPortalWeekRecord | null {
  const orderedWeeks = sortPublishedWeeks(snapshot);
  const currentIndex = orderedWeeks.findIndex((week) => week.matchDayId === selectedWeekId);
  if (currentIndex < 0) {
    return null;
  }
  return (
    orderedWeeks
      .slice(currentIndex + 1)
      .find((week) => week.matches.length > 0 || (Array.isArray(week.skinsRows) && week.skinsRows.length > 0)) ?? null
  );
}

export interface PortalWeekSkinsTableRow {
  key: string;
  playerName: string;
  subtitle: string | null;
  overallNetHoleScores: number[];
  overallNetScore: number;
  winningHoles: boolean[];
}

function getWeekSkinsRows(week: PublishedPortalWeekRecord): PublishedPortalWeekSkinsRowRecord[] {
  if (!Array.isArray(week.skinsRows)) {
    return [];
  }
  return week.skinsRows.filter(
    (row): row is PublishedPortalWeekSkinsRowRecord => Array.isArray(row?.overallNetHoleScores),
  );
}

function buildWinningHoleFlags(
  rows: PublishedPortalWeekSkinsRowRecord[],
  holeCount: number,
): boolean[][] {
  const bestScoresByHole = Array.from({ length: holeCount }, (_, holeIndex) => {
    const scores = rows
      .map((row) => row.overallNetHoleScores[holeIndex])
      .filter((score): score is number => typeof score === 'number' && Number.isFinite(score));
    if (scores.length === 0) {
      return null;
    }
    const lowestScore = Math.min(...scores);
    return scores.filter((score) => score === lowestScore).length === 1 ? lowestScore : null;
  });

  return rows.map((row) =>
    Array.from({ length: holeCount }, (_, holeIndex) => {
      const bestScore = bestScoresByHole[holeIndex];
      return bestScore !== null && row.overallNetHoleScores[holeIndex] === bestScore;
    }),
  );
}

export function buildPortalWeekSkinsTable(week: PublishedPortalWeekRecord): PortalWeekSkinsTableRow[] {
  const skinsRows = getWeekSkinsRows(week);
  if (skinsRows.length === 0) {
    return [];
  }

  const holeCount = skinsRows[0]?.overallNetHoleScores.length ?? 0;
  const winningHoleFlags = buildWinningHoleFlags(skinsRows, holeCount);

  return skinsRows.map((row, index) => ({
    key: `${row.playerId}-${row.for_player_id ?? 'competition'}-${index}`,
    playerName: row.playerName,
    subtitle:
      row.participant_type === 'sub'
        ? `Sub for ${row.for_player_name ?? 'scheduled player'}`
        : row.participant_type === 'standalone'
          ? 'Playoff week round'
          : null,
    overallNetHoleScores: [...row.overallNetHoleScores],
    overallNetScore: row.overallNetScore,
    winningHoles: winningHoleFlags[index] ?? Array.from({ length: holeCount }, () => false),
  }));
}
